import { useState, useEffect, useCallback, useMemo } from "react";
import { Pen2, CaseMinimalistic } from "@solar-icons/react/ssr";
import { Grid, useTheme, useMediaQuery } from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import format from "date-fns/format";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { useSearchParams, useParams } from "react-router-dom";
import styled from "styled-components";

import { request } from "@/Api";
import AiPromptBar from "@/ai/AiPromptBar";

import {
    PERMISSION_EDIT_PROJECTS,
    PERMISSION_VIEW_ADMIN_PANEL,
    PERMISSION_POST_FILES,
    PERMISSION_POST_BILLS,
    PERMISSION_VIEW_CONFIDENTIAL_DATA,
} from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import Comments from "@/common/Comments";
import { PageTitle } from "@/common/CommonComponents";
import { useSetOrgId } from "@/common/hooks";
import { HighlightedIcon } from "@/common/StyledComponents";
import { SectionTitle } from "@/common/typography";
import {
    FEATURE_MULTI_PERSON,
    FEATURE_PROJECT_FORECAST,
    FEATURE_BIDS,
    FEATURE_AI_INTAKE,
} from "@/constants";
import { openBidPopup, openProjectWorkshop } from "@/nav/navSlice";
import ProjectForecast from "@/project/ProjectForecast";
import ProjectLoading from "@/project/ProjectLoading";
import ProjectMilestones from "@/project/ProjectMilestones";
import ProjectFlow from "@/project_common/ProjectFlow";
import { confirmedBidStatuses } from "@/sheet/constants";
import { getCurrencySymbol } from "@/utils";
import ProjectBidsSection from "./ProjectBidsSection";
import ProjectBillsSection from "./ProjectBillsSection";
import ProjectDetailsSection from "./ProjectDetailsSection";
import ProjectFinancialsSection from "./ProjectFinancialsSection";
import ProjectShortcuts from "./ProjectShortcuts";
import ProjectThreadFiles from "./ProjectThreadFiles";
import { PROJECT_STATUS_IN_PRODUCTION } from "./constants";
import PurchaseOrders from "@/purchase_orders/PurchaseOrders";

const ProjectContainer = styled.div`
    padding-bottom: 150px;
`;

export const DocketWrapper = styled.div`
    display: flex;
    align-items: center;
    margin: 6px 0;

    h4 {
        color: grey;
        margin: 0 0 0 12px;
        font-weight: normal;
    }
`;

const FormWrapper = styled.div`
    margin: 24px 0;
`;

export default function Project() {
    const { projectId } = useParams();
    const { hasFeature, hasPermission } = useEntitlements();
    useSetOrgId();

    const [searchParams] = useSearchParams();
    const defaultMilestoneId = searchParams.get("milestone");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const dispatch = useDispatch();

    const [project, setProject] = useState({});
    const [cachedFieldValue, setCachedFieldValue] = useState(null);
    const [bids, setBids] = useState([]); // TODO: consider removing this state cause otherwise we might have to manage project.bids as well
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [isBidRefreshing, setIsBidRefreshing] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    const [showBillAlert, setShowBillAlert] = useState(false);

    const canUpdate = hasPermission(PERMISSION_EDIT_PROJECTS);
    const [updatePermission, setUpdatePermission] = useState(canUpdate);
    const updateGoogleIds = hasPermission(PERMISSION_VIEW_ADMIN_PANEL);
    const filesPermission = hasPermission(PERMISSION_POST_FILES);
    const billsPermission = hasPermission(PERMISSION_POST_BILLS);
    const confidentialDataPermission = hasPermission(
        PERMISSION_VIEW_CONFIDENTIAL_DATA,
    );
    const bidFiles = useMemo(
        () =>
            bids.reduce((files, bid) => {
                files = files.concat(bid.files);
                return files;
            }, []),
        [bids],
    );

    const [showReadOnlyAlert, setShowReadOnlyAlert] =
        useState(!updatePermission);

    const mapProject = useCallback((newProject) => {
        return {
            ...newProject,
            person_ids: newProject.persons.map((person) => person.id), // for multi autocomplete
        };
    }, []);

    const refreshProjectBidData = useCallback(() => {
        setIsBidRefreshing(true);

        request
            .post(`projects/${project.id}/refresh-gs-request`)
            .then((response) => {
                setProject(mapProject(response.data));
            })
            .finally(() => {
                setIsBidRefreshing(false);
            });
    }, [project.id, mapProject]);

    const getProject = useCallback(() => {
        request
            .get(`projects/${projectId}`)
            .then((response) => {
                setProject(mapProject(response.data));
                setBids(response.data.bids);

                if (response.data.access_level != "write") {
                    setUpdatePermission((previous) => {
                        if (previous) {
                            setShowReadOnlyAlert(true);
                        }
                        return false;
                    });
                }
            })
            .catch(function (error) {
                setUpdatePermission(false);
                if (error.response?.status == 403) {
                    setError("You do not have access to view this project.");
                } else {
                    setError(
                        "Failed to load project, please contact an admin.",
                    );
                }
            });
    }, [projectId, mapProject]);

    useEffect(() => {
        getProject();
    }, [getProject]);

    // Live-sync shortcuts added from the AI assistant
    useEffect(() => {
        const handleShortcutCreated = (event) => {
            const shortcut = event.detail;
            if (shortcut?.project_id === parseInt(projectId)) {
                setProject((prev) => ({
                    ...prev,
                    shortcuts: [...(prev.shortcuts || []), shortcut],
                }));
            }
        };
        window.addEventListener("shortcut:created", handleShortcutCreated);
        return () =>
            window.removeEventListener(
                "shortcut:created",
                handleShortcutCreated,
            );
    }, [projectId]);

    const saveProject = useCallback(
        (data) => {
            setErrors({});

            request
                .patch(`projects/${project.id}`, data)
                .then((response) => {
                    setProject(mapProject(response.data));
                    setShowSaved(true);
                })
                .catch(function (error) {
                    if (error.response.status == 422) {
                        const details = error.response.data.detail;
                        const currentErrors = {};
                        for (const i in details) {
                            const field =
                                details[i].loc[details[i].loc.length - 1];
                            const msg = details[i].msg;
                            if (details[i].loc[1] == "forecast_bills") {
                                if (!currentErrors.forecast_bills) {
                                    currentErrors.forecast_bills = {};
                                }
                                const index = details[i].loc[2];
                                currentErrors.forecast_bills[index] = {
                                    ...currentErrors.forecast_bills[index],
                                    [field]: msg,
                                };
                            } else {
                                currentErrors[field] = msg;
                            }
                        }
                        setErrors(currentErrors);
                    } else {
                        setError(
                            error.response?.data?.detail ||
                            "Failed to update project.",
                        );
                    }
                });
        },
        [project.id, mapProject],
    );

    const handleBillsChange = useCallback((bills) => {
        setProject((previous) => ({ ...previous, bills: [...bills] }));
    }, []);

    const handleInputChange = useCallback(
        (fieldName) => (event) => {
            const { value } = event.target;
            setProject((previous) => ({ ...previous, [fieldName]: value }));
        },
        [],
    );

    const handleTeamMemberChange = useCallback(
        (_event, persons) => {
            const person_ids = persons.map((person) => person.id);
            const personsClone = persons.map((person) => ({ ...person }));
            setProject((previous) => ({
                ...previous,
                person_ids,
                persons: personsClone,
            }));
            saveProject({ person_ids });
        },
        [saveProject],
    );

    const handleAutocompleteChange = useCallback(
        (fieldName, idFieldName) => (_event, newValue) => {
            let updatedData = null;
            if (idFieldName) {
                if (typeof newValue == "object") {
                    updatedData = {
                        [fieldName]: newValue,
                        [idFieldName]: newValue.id,
                    };
                } else if (typeof newValue == "number") {
                    updatedData = { [idFieldName]: newValue };
                }
            } else {
                updatedData = { [fieldName]: newValue };
            }
            if (updatedData) {
                setProject((previous) => ({ ...previous, ...updatedData }));
                saveProject({ ...updatedData });
            }
        },
        [saveProject],
    );

    const handlePopupOpen = useCallback(() => {
        dispatch(
            openBidPopup({
                type: "Bid Sheet",
                projectId: projectId,
            }),
        );
    }, [dispatch, projectId]);

    const handleCalendarChange = useCallback(
        (fieldName, save) => (newValue) => {
            let date = newValue;
            if (date != null) {
                try {
                    date = format(newValue, "y-MM-dd");
                } catch (e) {
                    date = "";
                }
            }

            setProject((previous) => ({ ...previous, [fieldName]: date }));

            if (save) {
                saveProject({ [fieldName]: date });
            }
        },
        [saveProject],
    );

    const cacheProjectField = useCallback(
        (field) => () => {
            setCachedFieldValue(project[field]);
        },
        [project],
    );

    const saveProjectField = useCallback(
        (field) => () => {
            if (cachedFieldValue != project[field]) {
                saveProject({ [field]: project[field] });
            }
            setCachedFieldValue(null);
        },
        [cachedFieldValue, project, saveProject],
    );

    const onStatusUpdate = useCallback(
        (statusId) => {
            setProject((previous) => ({ ...previous, status_id: statusId }));
            saveProject({ status_id: statusId });
        },
        [saveProject],
    );

    const handleShortcutCreate = useCallback((shortcut) => {
        setProject((previous) => ({
            ...previous,
            shortcuts: [...previous.shortcuts, shortcut],
        }));
    }, []);

    const handleShortcutUpdate = useCallback((shortcut) => {
        setProject((previous) => ({
            ...previous,
            shortcuts: previous.shortcuts.map((s) =>
                s.id == shortcut.id ? shortcut : s,
            ),
        }));
    }, []);

    const handleShortcutDelete = useCallback((shortcut) => {
        setProject((previous) => ({
            ...previous,
            shortcuts: previous.shortcuts.filter((s) => s.id != shortcut.id),
        }));
    }, []);

    const handleBriefOpen = useCallback(() => {
        dispatch(
            openProjectWorkshop({
                activeAgent: "brief",
                projectId: parseInt(projectId),
                projectName: project.name,
            }),
        );
    }, [dispatch, projectId, project.name]);

    const forecastValue = useMemo(
        () => ({
            forecast_revenue: project.forecast_revenue,
            forecast_schedule: project.forecast_schedule,
            forecast_certainty: project.forecast_certainty,
            forecast_bills: project.forecast_bills || [],
        }),
        [
            project.forecast_revenue,
            project.forecast_schedule,
            project.forecast_certainty,
            project.forecast_bills,
        ],
    );

    const forecastErrors = useMemo(
        () => ({
            forecast_revenue: errors.forecast_revenue,
            forecast_schedule: errors.forecast_schedule,
            forecast_certainty: errors.forecast_certainty,
            forecast_bills: errors.forecast_bills || {},
        }),
        [
            errors.forecast_revenue,
            errors.forecast_schedule,
            errors.forecast_certainty,
            errors.forecast_bills,
        ],
    );

    const setForecast = useCallback((forecast) => {
        setProject((previous) => ({ ...previous, ...forecast }));
    }, []);

    const setForecastErrors = useCallback((forecastErrorsUpdate) => {
        setErrors((previous) => ({ ...previous, ...forecastErrorsUpdate }));
    }, []);

    const setMilestones = useCallback((milestones) => {
        setProject((previous) => ({ ...previous, milestones: milestones }));
    }, []);

    const showSaveAlert = useCallback(() => {
        setShowSaved(true);
    }, []);

    const handleSavedClose = useCallback(() => {
        setShowSaved(false);
    }, []);

    const handleBillAlertClose = useCallback(() => {
        setShowBillAlert(false);
    }, []);

    const handleErrorClose = useCallback(() => {
        setError("");
    }, []);

    const handleActualizeOpen = useCallback(() => {
        window.location = `/projects/${projectId}/actuals`;
    }, [projectId]);

    const canActualize = useMemo(
        () => bids.some((bid) => confirmedBidStatuses.includes(bid.status)),
        [bids],
    );

    const combinedFiles = useMemo(
        () => [...(project.files || []), ...bidFiles],
        [project.files, bidFiles],
    );

    const defaultMilestone = useMemo(
        () =>
            (project.milestones || []).find(
                (milestone) => milestone.id == defaultMilestoneId,
            ),
        [project.milestones, defaultMilestoneId],
    );

    const onPurchaseOrderAdd = (purchaseOrder) => {
        if (project.purchase_orders.find((p) => p.id == purchaseOrder.id)) {
            onPurchaseOrderUpdate(purchaseOrder);
            return;
        }
        setProject((currenctProject) => {
            return {
                ...currenctProject,
                purchase_orders: [
                    ...currenctProject.purchase_orders,
                    { ...purchaseOrder, _reminded: true },
                ],
            };
        });
    };

    const onPurchaseOrderUpdate = (purchaseOrder) => {
        setProject((currentProject) => {
            return {
                ...currentProject,
                purchase_orders: currentProject.purchase_orders.map((p) => ({
                    ...p,
                    _reminded: p.id == purchaseOrder.id ? true : p._reminded,
                })),
            };
        });
    };

    const onPurchaseOrderDelete = (purchaseOrder) => {
        setProject({
            ...project,
            purchase_orders: project.purchase_orders.filter(
                (p) => p.id != purchaseOrder.id,
            ),
        });
    };

    return Object.keys(project).length == 0 ? (
        <ProjectLoading />
    ) : (
        <ProjectContainer>
            <Helmet>
                <PageTitle title={project.name} />
            </Helmet>

            <DocketWrapper>
                <HighlightedIcon icon={CaseMinimalistic} highlighted />
                <h4>{project.docket}</h4>
            </DocketWrapper>

            <TextField
                value={project.name || ""}
                onChange={handleInputChange("name")}
                onFocus={cacheProjectField("name")}
                onBlur={saveProjectField("name")}
                placeholder="Project Name"
                variant="standard"
                InputProps={{
                    style: {
                        fontSize: isMobile
                            ? "22px"
                            : theme.typography.h1.fontSize,
                    },
                    readOnly: !updatePermission,
                    disableUnderline: true,
                }}
                InputLabelProps={{
                    style: { fontSize: isMobile ? "22px" : "36px" },
                }}
                fullWidth
                data-testid="project-name"
            />

            <FormWrapper>
                <Grid
                    container
                    spacing={2}
                    justifyContent={"space-between"}
                    flexDirection={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "stretch" : ""}
                    sx={{ marginBottom: "36px" }}
                >
                    <Grid item flexGrow={1}>
                        <ProjectFlow
                            onStatusUpdate={onStatusUpdate}
                            project={project}
                            mode={"project"}
                        />
                    </Grid>
                    {hasFeature(FEATURE_AI_INTAKE) && (
                        <Grid item>
                            <Button
                                variant="outlined"
                                fullWidth={isMobile}
                                onClick={() =>
                                    dispatch(
                                        openProjectWorkshop({
                                            projectId: parseInt(projectId),
                                            projectName: project.name,
                                        }),
                                    )
                                }
                            >
                                Previous Chats
                            </Button>
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ mb: 4 }}>
                    <AiPromptBar
                        onSendPrompt={(text) =>
                            dispatch(
                                openProjectWorkshop({
                                    projectId: parseInt(projectId),
                                    projectName: project.name,
                                    taskSummary: text,
                                }),
                            )
                        }
                        onStartWorkflow={(agentType) =>
                            dispatch(
                                openProjectWorkshop({
                                    projectId: parseInt(projectId),
                                    projectName: project.name,
                                    activeAgent: agentType,
                                }),
                            )
                        }
                    />
                </Box>

                <ProjectDetailsSection
                    project={project}
                    updatePermission={updatePermission}
                    errors={errors}
                    handleInputChange={handleInputChange}
                    cacheProjectField={cacheProjectField}
                    saveProjectField={saveProjectField}
                    handleAutocompleteChange={handleAutocompleteChange}
                    handleCalendarChange={handleCalendarChange}
                    handleTeamMemberChange={handleTeamMemberChange}
                />

                <SectionTitle>Shortcuts</SectionTitle>
                {project && (
                    <ProjectShortcuts
                        onShortcutCreate={handleShortcutCreate}
                        onShortcutUpdate={handleShortcutUpdate}
                        onShortcutDelete={handleShortcutDelete}
                        shortcuts={project.shortcuts}
                    />
                )}

                <ProjectThreadFiles projectId={projectId} />

                {hasFeature(FEATURE_BIDS) && (
                    <>
                        <Divider sx={{ marginTop: 5, marginBottom: 5 }} />
                        <ProjectFinancialsSection
                            project={project}
                            confidentialDataPermission={
                                confidentialDataPermission
                            }
                            isMobile={isMobile}
                        />
                    </>
                )}

                {hasFeature(FEATURE_PROJECT_FORECAST) && (
                    <ProjectForecast
                        forecast={forecastValue}
                        setForecast={setForecast}
                        errors={forecastErrors}
                        setErrors={setForecastErrors}
                        currency={getCurrencySymbol(project.currency || "CAD")}
                        cacheForecastField={cacheProjectField}
                        saveForecastField={saveProjectField}
                        saveForecast={saveProject}
                        showSaveAlert={showSaveAlert}
                        project_id={projectId}
                        defaultExpanded={project.confirmed_bid == null}
                        readOnly={!updatePermission}
                    />
                )}

                {hasFeature(FEATURE_BIDS) && (
                    <ProjectBidsSection
                        project={project}
                        bids={bids}
                        setBids={setBids}
                        confidentialDataPermission={confidentialDataPermission}
                        updatePermission={updatePermission}
                        updateGoogleIds={updateGoogleIds}
                        isBidRefreshing={isBidRefreshing}
                        refreshProjectBidData={refreshProjectBidData}
                        handleActualizeOpen={handleActualizeOpen}
                        canActualize={canActualize}
                        combinedFiles={combinedFiles}
                        filesPermission={filesPermission}
                        handleInputChange={handleInputChange}
                        theme={theme}
                        handlePopupOpen={handlePopupOpen}
                    />
                )}

                {/* <ProjectBillsSection
                    project={project}
                    billsPermission={billsPermission}
                    updatePermission={updatePermission}
                    handleBillsChange={handleBillsChange}
                    showSaveAlert={showSaveAlert}
                /> */}
            </FormWrapper>

            {/* <Divider sx={{ marginTop: 5, marginBottom: 5 }} /> */}

            {/* <PurchaseOrders
                variant={"project"}
                editable={project.status.name == PROJECT_STATUS_IN_PRODUCTION}
                purchaseOrders={project.purchase_orders}
                onAdd={onPurchaseOrderAdd}
                onUpdate={onPurchaseOrderUpdate}
                onDelete={onPurchaseOrderDelete}
            /> */}

            <ProjectMilestones
                defaultMilestone={defaultMilestone}
                projectId={projectId}
                milestones={project.milestones}
                setMilestones={setMilestones}
                readOnly={!updatePermission}
            />

            {confidentialDataPermission && hasFeature(FEATURE_MULTI_PERSON) ? (
                <>
                    <Divider sx={{ marginTop: 5, marginBottom: 5 }} />
                    <SectionTitle>Comments</SectionTitle>
                    <Grid container>
                        <Grid item md={8} sm={12}>
                            <Comments
                                comments={project.comments || []}
                                modelType={"project"}
                                modelInstanceId={project.id}
                            />
                        </Grid>
                    </Grid>
                </>
            ) : null}

            <Snackbar
                open={showSaved}
                autoHideDuration={2000}
                onClose={handleSavedClose}
                message="Project saved"
            />

            <Snackbar
                open={showBillAlert}
                autoHideDuration={2000}
                onClose={handleBillAlertClose}
            >
                <Alert severity="error">
                    Please create bill schedule first
                </Alert>
            </Snackbar>

            {confidentialDataPermission ? (
                <Snackbar open={showReadOnlyAlert}>
                    <Alert
                        severity="warning"
                        variant="filled"
                        onClose={() => setShowReadOnlyAlert(false)}
                    >
                        You are restricted to read only access for this project.
                    </Alert>
                </Snackbar>
            ) : null}

            <Snackbar
                open={error != ""}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={handleErrorClose}
                >
                    {error}
                </Alert>
            </Snackbar>
        </ProjectContainer>
    );
}
